// ----- Phase 5.X-document-handling-enhancements (Feature 3) — Cover-letter sync banner -----
//
// Renders a warning banner on the document inspector when:
//   - The document is a cover letter (CtdSectionCode === '1.2') AND
//   - The backend's sync-status route returns 'out-of-sync' AND
//   - The user's `autoSyncCoverLetter` setting (post 3-tier merge) is true.
//
// All three conditions must hold. The setting fetch is parallel with
// the sync-status fetch — we don't want to delay the banner waiting
// for one or the other.
//
// "Mark as up to date" calls the snapshot POST and re-fetches the
// status; the banner disappears on success.

import { useEffect, useState, type CSSProperties, type ReactElement } from 'react';
import {
  ApiError,
  parseApiErrorBody,
  v2,
  type CoverLetterSyncStatus,
} from '@/api/v2Client';

export interface CoverLetterSyncBannerProps {
  readonly documentId: string;
  readonly ctdSectionCode: string | null;
  /** T-17 (D6) — the document's submission, so a submission-level
   *  `autoSyncCoverLetter` override decides whether the banner shows. */
  readonly submissionId?: string | undefined;
  /** Pass false to suppress the banner regardless of status — e.g., from
   *  a parent that already knows the setting is off. */
  readonly enabled?: boolean;
  readonly canEdit: boolean;
  /** Called on successful "Mark as up to date" — parent reloads. */
  readonly onSnapshotCaptured?: () => void;
  readonly onError?: (message: string) => void;
}

const bannerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  padding: '10px 14px',
  background: '#fef3c7',
  border: '1px solid #fcd34d',
  borderRadius: 6,
  color: '#92400e',
  fontSize: 13,
  marginBottom: 12,
};

const buttonStyle: CSSProperties = {
  background: '#92400e',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  padding: '4px 10px',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  flexShrink: 0,
};

const buttonDisabledStyle: CSSProperties = {
  ...buttonStyle,
  opacity: 0.5,
  cursor: 'not-allowed',
};

/** Resolve the `autoSyncCoverLetter` setting from the merged effective
 *  map. Defaults to `false` per V1 — opt-in to the warning. Only a real
 *  boolean counts — the same rule the server's resolver applies (a stored
 *  string "true" resolves to the default there, so it must here too). */
function resolveAutoSyncSetting(merged: Record<string, { Value: unknown }> | null): boolean {
  if (!merged) return false;
  const raw = merged['autoSyncCoverLetter']?.Value;
  return typeof raw === 'boolean' ? raw : false;
}

export function CoverLetterSyncBanner({
  documentId,
  ctdSectionCode,
  submissionId,
  enabled,
  canEdit,
  onSnapshotCaptured,
  onError,
}: CoverLetterSyncBannerProps): ReactElement | null {
  const isCoverLetter = ctdSectionCode === '1.2';
  const [status, setStatus] = useState<CoverLetterSyncStatus | null>(null);
  const [settingEnabled, setSettingEnabled] = useState<boolean | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Fetch sync status + setting in parallel. Both are cheap; we don't
  // gate one on the other.
  useEffect(() => {
    if (!isCoverLetter) return;
    let cancelled = false;
    v2.documents
      .coverLetterSyncStatus(documentId)
      .then((res) => {
        if (!cancelled) setStatus(res);
      })
      .catch(() => {
        // Failure is silent — the banner just doesn't render. The user
        // can navigate away / refresh; we don't want to spam errors.
        if (!cancelled) setStatus(null);
      });
    // T-17 (D6) — submission-merged when the submission is known.
    (submissionId ? v2.settings.getSubmission(submissionId) : v2.settings.listAll())
      .then((res) => {
        if (!cancelled) setSettingEnabled(resolveAutoSyncSetting(res.settings));
      })
      .catch(() => {
        if (!cancelled) setSettingEnabled(false);
      });
    return (): void => {
      cancelled = true;
    };
  }, [documentId, isCoverLetter, submissionId, reloadKey]);

  // Allow a parent toggle to short-circuit before we even render.
  if (enabled === false) return null;
  if (!isCoverLetter) return null;
  if (!status || settingEnabled === null) return null;
  if (!settingEnabled) return null;
  if (status.status !== 'out-of-sync') return null;

  const handleSnapshot = async (): Promise<void> => {
    if (!canEdit) return;
    setCapturing(true);
    try {
      await v2.documents.coverLetterSnapshot(documentId);
      setReloadKey((k) => k + 1);
      onSnapshotCaptured?.();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? (parseApiErrorBody(err)?.message ?? err.message)
          : err instanceof Error
            ? err.message
            : 'Failed to capture snapshot';
      onError?.(msg);
    } finally {
      setCapturing(false);
    }
  };

  const addedN = status.deltaDocs.added.length;
  const removedN = status.deltaDocs.removed.length;
  const summary =
    `${addedN} added, ${removedN} removed since generation` +
    (status.generatedAt ? ` (${new Date(status.generatedAt).toLocaleString()})` : '');

  return (
    <div role="alert" data-testid="cover-letter-sync-banner" style={bannerStyle}>
      <div>
        <strong>⚠️ Cover letter may be out of sync</strong>
        <div style={{ marginTop: 2, fontSize: 12 }}>{summary}</div>
      </div>
      <button
        type="button"
        data-testid="cover-letter-mark-up-to-date-btn"
        style={canEdit && !capturing ? buttonStyle : buttonDisabledStyle}
        disabled={!canEdit || capturing}
        onClick={() => void handleSnapshot()}
        title={
          canEdit
            ? 'Capture the current document list as the cover letter\'s new snapshot.'
            : 'Requires Documents:write permission.'
        }
      >
        {capturing ? 'Capturing…' : 'Mark as up to date'}
      </button>
    </div>
  );
}
